import { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { WhisperAIService } from '@/services/whisperAiService';
import { AudioWaveform } from './AudioWaveform';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMicrophone as fasMicrophone,
} from "@fortawesome/free-solid-svg-icons";
interface WhisperSpeechToTextProps {
  onTranscript: (text: string) => void;
  isDarkMode: boolean;
  className?: string;
  onListeningChange?: (isListening: boolean) => void;
}

export const WhisperSpeechToText = ({
  onTranscript,
  isDarkMode = false,
  className = '',
  onListeningChange,
}: WhisperSpeechToTextProps) => {
  // State
  const [isListening, setIsListening] = useState(false);
  
  // Notify parent component when listening state changes
  useEffect(() => {
    onListeningChange?.(isListening);
  }, [isListening, onListeningChange]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationRef = useRef<number | null>(null);
  const mimeTypeRef = useRef<string>('audio/webm');
  
  // Hooks
  const { toast } = useToast();

  // Cleanup function for audio resources
  const cleanupStream = useCallback(() => {
    // Clear timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Stop any animation frame
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    // Disconnect audio nodes
    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
      } catch (e) {
        // Error disconnecting source
      }
      sourceRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      if (audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(console.error);
      }
      audioContextRef.current = null;
    }

    // Stop media recorder
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch (e) {
          // Error stopping media recorder
        }
      }
      mediaRecorderRef.current = null;
    }

    // Stop all media tracks
    if (streamRef.current) {
      const tracks = streamRef.current.getTracks();
      tracks.forEach(track => {
        try {
          track.stop();
        } catch (e) {
          // Error stopping track
        }
      });
      streamRef.current = null;
    }
    
    // Clear any pending chunks
    chunksRef.current = [];
    
    // Reset state
    setIsListening(false);
    setIsProcessing(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupStream();
    };
  }, [cleanupStream]);

  // Timeout for 50-second limit
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Start listening function
  const startListening = useCallback(async () => {
    try {
      // Reset any existing state
      cleanupStream();
      setIsProcessing(true);
      
      // Get user media
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      // Set up audio context
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      // Create audio source from stream
      const source = audioContext.createMediaStreamSource(stream);
      sourceRef.current = source;
      
      // Set up media recorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : '';
      
      if (!mimeType) {
        throw new Error('No supported MIME type for MediaRecorder');
      }
      
      mimeTypeRef.current = mimeType;
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      
      // Handle data available
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      // Handle recording stop
      mediaRecorder.onstop = async () => {
        const processAudio = async () => {
          try {
            setIsProcessing(true);
            
            // Create a copy of the chunks and clear the ref
            const audioChunks = [...chunksRef.current];
            chunksRef.current = [];
            
            // Check if there's any audio data
            if (audioChunks.length === 0) {
              return;
            }
            
            // Create a blob from the chunks
            const audioBlob = new Blob(audioChunks, { type: mimeTypeRef.current });
            
            // Check if the audio recording is too short
            if (audioBlob.size < 1000) {
              return;
            }
            
            // Transcribe the audio
            const transcript = await WhisperAIService.transcribeAudio(audioBlob);
            
            // If there's a transcript, call the onTranscript callback
            if (transcript?.trim()) {
              onTranscript(transcript.trim());
            } else {
            }
          } catch (error) {
            toast({
              title: "Transcription Error",
              description: "Failed to transcribe audio. Please try again.",
              variant: "destructive"
            });
          } finally {
            setIsProcessing(false);
            cleanupStream();
          }
        };
        
        // Process in the next tick to avoid blocking UI
        setTimeout(processAudio, 0);
      };
      
      // Handle errors
      mediaRecorder.onerror = (event) => {
        toast({
          title: "Recording Error",
          description: "An error occurred while recording audio.",
          variant: "destructive"
        });
        setIsListening(false);
        setIsProcessing(false);
        cleanupStream();
      };
      
      // Start recording
      mediaRecorder.start(1000); // Collect data every second
      setIsListening(true);
      setHasInitialized(true);

      // Set 50-second timeout
      timeoutRef.current = setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          try {
            mediaRecorderRef.current.requestData();
            mediaRecorderRef.current.stop();
          } catch (error) {
            // Error auto-stopping recording
          }
        }
      }, 50000); // 50 seconds
      
    } catch (error) {
      const err = error as Error & { name?: string };
      let errorMessage = "Please allow microphone access to use speech recognition.";
      
      // Handle specific errors
      if (err.name === 'NotAllowedError') {
        errorMessage = "Microphone access denied. Please allow microphone permissions.";
      } else if (err.name === 'NotFoundError') {
        errorMessage = "No microphone found. Please check your microphone connection.";
      }
      
      // Show an error toast
      toast({
        title: "Microphone Error",
        description: errorMessage,
        variant: "destructive"
      });
      
      setIsListening(false);
      setIsProcessing(false);
      setHasInitialized(false);
    }
  }, [cleanupStream, onTranscript, toast]);

  // Toggle listening state
  const toggleListening = useCallback(async () => {
    // Don't allow starting a new recording while processing
    if (isProcessing && !isListening) return;
    
    // If we're currently listening, stop the media recorder
    if (isListening) {
      // Immediately update UI to show we're stopping
      setIsProcessing(true);
      
      try {
        // Stop the media recorder if it exists
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          try {
            mediaRecorderRef.current.requestData();
            mediaRecorderRef.current.stop();
          } catch (error) {
            // Error stopping media recorder
          }
          
          // Safely close audio context if it exists
          if (audioContextRef.current) {
            try {
              await audioContextRef.current.close();
            } catch (error) {
              // Error closing audio context
            } finally {
              audioContextRef.current = null;
            }
          }
        }
        
        // Stop all tracks immediately without waiting
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => {
            try {
              track.stop();
              if (streamRef.current) {
                streamRef.current.removeTrack(track);
              }
            } catch (e) {
              // Error stopping track
            }
          });
          streamRef.current = null;
        }
      } catch (error) {
        // Error stopping recording
      } finally {
        // Reset state
        setIsListening(false);
        setIsProcessing(false);
        setHasInitialized(false);
      }
    } else {
      // Start listening
      setHasInitialized(false);
      try {
        await startListening();
      } catch (error) {
        setIsProcessing(false);
        setHasInitialized(false);
      }
    }
  }, [isListening, isProcessing, startListening]);

  return (
    <div className={` flex items-center rounded-md w-full flex flex-row items-end gap-2`}>
      {/* Waveform positioned at the top */}
      <div className={`w-full transition-all duration-200 overflow-hidden ${
        isListening ? 'h-[35px] opacity-100 pl-2' : 'h-0 opacity-0'
      }`}>
        <div className="h-full bg-gray-200/80 dark:bg-[#30303080] rounded-[15px] overflow-hidden">
          {isListening && (
            <AudioWaveform
              audioContext={audioContextRef.current}
              stream={streamRef.current}
              isActive={isListening}
              isDarkMode={isDarkMode}
              className="w-full h-full"
            />
          )}
        </div>
      </div>
      
      <div className="flex items-center justify-end gap-2 mt-0 relative group">
        {isListening && (
          <div className="w-0 hidden">
          </div>
        )}
        
        <button
          type="button"
          onClick={toggleListening}
          disabled={isProcessing && !isListening}
          className={`inline-flex border-none items-center justify-center rounded-full w-8 h-8 border transition-all duration-200 focus:outline-none bg-bg backdrop-blur-sm ${
            isListening 
              ? `bg-red-500 hover:bg-red-600 text-white focus:ring-red-500 border-transparent` 
              : `${isDarkMode ? 'border-gray-600 hover:bg-[#303030] text-white' : 'hover:bg-gray-200 text-gray-800 border-transparent'}`
          } ${(isProcessing && !isListening) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          {isProcessing && !isListening ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isListening ? (
            <div className="h-3 w-3 bg-current rounded-sm"></div>
          ) : (
            <FontAwesomeIcon icon={fasMicrophone} style={{color: isDarkMode ? "#ffffff" : "#000000"}} />
          )}
          <span className="sr-only">
            {isProcessing ? 'Processing...' : isListening ? 'Stop Recording' : 'Start Recording'}
          </span>
        </button>
        
        {/* Tooltip */}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 text-xs rounded-md bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
          {isProcessing ? 'Done' : isListening ? 'Transcribing...' : 'Dictate'}
        </div>
      </div>
    </div>
  );
};

export default WhisperSpeechToText;